import React, { useRef, useEffect, useState, useMemo, memo, forwardRef, useImperativeHandle, useCallback } from 'react';
import useUserCredentials from '../../../utils/hooks/user/useUserCredentials';
import { UserRole } from '../../../utils/api/endpoints/user/credentials';
import { getVantaStatus } from '../../../utils/api/endpoints/vanta';
import PDFDownloader from './PDFDownloader';
import { ReportData } from '../types/report.types';
import { pdf } from '@react-pdf/renderer';
import ReportTemplatePDF from './ReportTemplatePDF';
import FullReportTemplatePDF from './FullReportTemplatePDF';
import ExecutiveSummaryTemplatePDF from './ExecutiveSummaryTemplatePDF';
import ExecutiveSummaryTemplatePDF2 from './ExecutiveSummaryTemplatePDF2';
import ReportTemplatePDF2 from './ReportTemplatePDF2';
import FullReportTemplatePDF2 from './FullReportTemplatePDF2';
import ChartImageGenerator from './ChartImageGenerator';
import { parseHtmlToElements, preWarmImages } from './pdfHtmlImageUtils';
import { getAppropriatePreviewMode, getAvailablePreviewModes, isFindingClosed, processAttachmentsToElements, getFindingStableKey, isFindingActuallyOpen, filterReportDataByRunId } from '../utils/findingsUtils';
import { registerReportFonts } from '../utils/reportSharedUtils';

// Register fonts once globally
registerReportFonts();

interface PreviewSectionProps {
  previewLoading?: boolean;
  reportData?: ReportData;
  initialPreviewMode?: 'full' | 'technical' | 'executive';
  onPreviewModeChange?: (mode: 'full' | 'technical' | 'executive') => void;
  // Vanta controls (optional)
  onUploadToVanta?: () => void;
  vantaUploadLoading?: boolean;
  vantaUploadProgress?: number;
  hideHeader?: boolean;
}

// PDF Blob Viewer Component - generates PDF once and displays in iframe
const PDFBlobViewer = memo(({
  pdfKey,
  previewMode,
  snapshotData,
  processedFindings,
  pdfProcessing,
  pieChartImage,
  barChartImage,
  template,
}: {
  pdfKey: number;
  previewMode: 'full' | 'technical' | 'executive';
  snapshotData: ReportData;
  processedFindings: Record<string, any[]> | null;
  pdfProcessing: boolean;
  pieChartImage: string;
  barChartImage: string;
  template?: string;
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const modeUrlsRef = useRef<Record<string, { url: string; key: number }>>({});
  const lastKeyRef = useRef<number>(pdfKey);
  const staleUrlsRef = useRef<string[]>([]);

  // If pdfKey changed (data updated), queue previous mode URLs for delayed safe revocation
  // so the active iframe does not flash blank or show network error while generating new PDF
  if (lastKeyRef.current !== pdfKey) {
    Object.values(modeUrlsRef.current).forEach(({ url }) => {
      if (url && !staleUrlsRef.current.includes(url)) {
        staleUrlsRef.current.push(url);
      }
    });
    modeUrlsRef.current = {};
    lastKeyRef.current = pdfKey;
  }

  // Cleanup all mode URLs when unmounting
  useEffect(() => {
    return () => {
      Object.values(modeUrlsRef.current).forEach(({ url }) => {
        try { URL.revokeObjectURL(url); } catch (_) {}
      });
      staleUrlsRef.current.forEach(url => {
        try { URL.revokeObjectURL(url); } catch (_) {}
      });
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    // 1. Instant tab-switching: if we already generated a PDF for this previewMode and key, reuse it!
    const cachedForMode = modeUrlsRef.current[previewMode];
    if (cachedForMode && cachedForMode.key === pdfKey) {
      setPdfUrl(cachedForMode.url);
      setGenerating(false);
      return;
    }

    // IMPORTANT: Wait if findings are still actively being processed async.
    // Prevents redundant PDF generation that produces dummy "Loading..." page.
    if (pdfProcessing) {
      return;
    }

    const generatePDF = async () => {
      setGenerating(true);

      try {
        // Pre-warm image cache for scope fields (finding images are already cached by processAllFindings)
        const scopeFields: (string | undefined | null)[] = [snapshotData.scope, snapshotData.scope_notes];
        await preWarmImages(scopeFields);

        // Select the appropriate template based on previewMode and template version
        const isV2 = template === 'template-2';
        let document;
        if (previewMode === 'full') {
          document = isV2 ? (
            <FullReportTemplatePDF2
              reportData={snapshotData}
              processedFindings={processedFindings}
              processing={false}
              pieChartImage={pieChartImage}
              barChartImage={barChartImage}
            />
          ) : (
            <FullReportTemplatePDF
              reportData={snapshotData}
              processedFindings={processedFindings}
              processing={false}
              pieChartImage={pieChartImage}
              barChartImage={barChartImage}
            />
          );
        } else if (previewMode === 'executive') {
          document = isV2 ? (
            <ExecutiveSummaryTemplatePDF2
              reportData={snapshotData}
              processing={false}
              pieChartImage={pieChartImage}
              barChartImage={barChartImage}
            />
          ) : (
            <ExecutiveSummaryTemplatePDF
              reportData={snapshotData}
              processing={false}
            />
          );
        } else {
          document = isV2 ? (
            <ExecutiveSummaryTemplatePDF2
              reportData={snapshotData}
              processing={false}
              pieChartImage={pieChartImage}
              barChartImage={barChartImage}
            />
          ) : (
            <ReportTemplatePDF
              reportData={snapshotData}
              processedFindings={processedFindings}
              processing={false}
              pieChartImage={pieChartImage}
              barChartImage={barChartImage}
            />
          );
        }

        // Generate PDF blob
        const blob = await pdf(document).toBlob();

        if (!cancelled) {
          const newUrl = URL.createObjectURL(blob);
          const oldUrlForMode = modeUrlsRef.current[previewMode]?.url;
          if (oldUrlForMode && oldUrlForMode !== newUrl) {
            staleUrlsRef.current.push(oldUrlForMode);
          }
          modeUrlsRef.current[previewMode] = { url: newUrl, key: pdfKey };
          setPdfUrl(newUrl);

          // Once newUrl is mounted, revoke stale URLs safely after a 1.5s delay
          const toRevoke = [...staleUrlsRef.current];
          staleUrlsRef.current = [];
          if (toRevoke.length > 0) {
            setTimeout(() => {
              toRevoke.forEach(url => {
                if (url !== newUrl) {
                  try { URL.revokeObjectURL(url); } catch (_) {}
                }
              });
            }, 1500);
          }
        }
      } catch (error) {
        console.error('❌ Error generating PDF:', error);
      } finally {
        if (!cancelled) {
          setGenerating(false);
        }
      }
    };

    generatePDF();

    return () => {
      cancelled = true;
    };
  }, [pdfKey, previewMode, snapshotData, processedFindings, pdfProcessing, pieChartImage, barChartImage, template]);

  if (!pdfUrl) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Generating PDF preview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full min-h-[600px]">
      {generating && (
        <div className="absolute top-3 right-3 z-30 bg-blue-600/90 backdrop-blur-md text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2 pointer-events-none animate-pulse">
          <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
          <span>Updating preview...</span>
        </div>
      )}
      <iframe
        key={pdfUrl}
        src={pdfUrl}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          minHeight: '600px'
        }}
        title="PDF Preview"
      />
    </div>
  );
});

const PreviewSection = memo(forwardRef<any, PreviewSectionProps>(({ previewLoading = false, reportData, initialPreviewMode, onPreviewModeChange, onUploadToVanta, vantaUploadLoading, vantaUploadProgress, hideHeader = false }, ref) => {
  // Store a stable snapshot of reportData that only updates on manual refresh
  const [snapshotData, setSnapshotData] = useState<ReportData | undefined>(undefined);
  const [hasUnrefreshedChanges, setHasUnrefreshedChanges] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // Track if initial render is done

  const [previewMode, setPreviewMode] = useState<'full' | 'technical' | 'executive'>(() => {
    if (initialPreviewMode) {
      return initialPreviewMode;
    }
    return 'executive';
  }); // New state for preview mode
  const [pdfKey, setPdfKey] = useState(0); // Force PDF re-render
  const [pdfProcessing, setPdfProcessing] = useState(false); // Track PDF processing state
  const [pendingDownload, setPendingDownload] = useState(false); // Track if user requested download while processing
  const pdfDownloaderRef = useRef<any>(null); // Ref to PDFDownloader

  const pdfProcessingRef = useRef(pdfProcessing);
  useEffect(() => {
    pdfProcessingRef.current = pdfProcessing;
  }, [pdfProcessing]);

  useImperativeHandle(ref, () => ({
    async downloadPDF() {
      if (pdfProcessingRef.current) {
        setPendingDownload(true);
        while (pdfProcessingRef.current) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      if (pdfDownloaderRef.current && pdfDownloaderRef.current.downloadPDF) {
        await pdfDownloaderRef.current.downloadPDF();
      }
    }
  }));
  // User role and Vanta connection status (for conditional Vanta button)
  const { id: userId, role } = useUserCredentials();
  const [vantaConnected, setVantaConnected] = useState<boolean>(false);
  useEffect(() => {
    let mounted = true;
    const checkStatus = async () => {
      try {
        if (role === UserRole.BUSINESS && userId) {
          const status = await getVantaStatus(userId);
          const connected = (status?.vanta_connected === true) || (status?.vanta_connected === 1) || (status?.connected === true);
          if (mounted) setVantaConnected(!!connected);
        } else {
          if (mounted) setVantaConnected(false);
        }
      } catch {
        if (mounted) setVantaConnected(false);
      }
    };
    checkStatus();
    return () => { mounted = false; };
  }, [role, userId]);

  const [processedFindings, setProcessedFindings] = useState<Record<string, any[]> | null>(null);
  const [pieChartImage, setPieChartImage] = useState<string | undefined>();
  const [barChartImage, setBarChartImage] = useState<string | undefined>();

  // Sync state with initialPreviewMode prop when it changes
  useEffect(() => {
    if (initialPreviewMode) {
      setPreviewMode(initialPreviewMode);
    }
  }, [initialPreviewMode]);

  // Initialize snapshot ONLY ONCE on first load - never automatically update
  useEffect(() => {
    if (reportData && !isInitialized) {
      setSnapshotData(filterReportDataByRunId(reportData));
      setIsInitialized(true);
    }
  }, [reportData, isInitialized]);

  // Detect when reportData changes (user made edits)
  // Use a ref to track the last reportData to avoid triggering on every render
  const lastReportDataRef = useRef(reportData);
  useEffect(() => {
    if (reportData && reportData !== lastReportDataRef.current) {
      lastReportDataRef.current = reportData;
      if (snapshotData && reportData !== snapshotData && isInitialized) {
        setHasUnrefreshedChanges(true);
      }
    }
  }, [reportData, snapshotData, isInitialized]);

  // Update preview mode when snapshotData becomes available or changes
  useEffect(() => {
    if (snapshotData && !initialPreviewMode) {
      const mode = getAppropriatePreviewMode(snapshotData);
      setPreviewMode(mode);
    }
  }, [snapshotData, initialPreviewMode]); // Update when snapshot changes

  // Handle preview mode changes
  const handlePreviewModeChange = (mode: 'full' | 'technical' | 'executive') => {
    setPreviewMode(mode);
    onPreviewModeChange?.(mode);
  };

  // Track chart data string to detect if counts actually changed
  const lastChartDataRef = useRef<string>('');
  // Cache for processed finding HTML elements to avoid expensive re-parsing on every refresh
  const parsedFindingCache = useRef<Map<string, any>>(new Map());

  // Force PDF refresh - update snapshot with latest data
  const handlePdfRefresh = useCallback(() => {
    console.log('🔄 PDF Refresh Triggered');
    if (reportData) {
      const filtered = filterReportDataByRunId(reportData);
      setSnapshotData(filtered); // Update snapshot with latest data
      setHasUnrefreshedChanges(false); // Clear the indicator

      // Only regenerate chart images if severity counts actually changed!
      const currentChartData = JSON.stringify(filtered?.open_close_counts_by_severity || {});
      if (lastChartDataRef.current !== currentChartData) {
        lastChartDataRef.current = currentChartData;
        setPieChartImage(undefined);
        setBarChartImage(undefined);
      }
    }
    setPdfKey(prev => prev + 1);
  }, [reportData]);


  // Reference cache to avoid recalculating findings when detailed_findings & config are identical
  const lastDetailedFindingsRef = useRef<any>(null);
  const lastConfigRef = useRef<any>(null);
  const lastProcessedResultRef = useRef<Record<string, any[]> | null>(null);

  // Process findings ONLY when:
  // 1. Initial load (isInitialized becomes true) - ONE TIME ONLY
  // 2. Manual refresh or auto-refresh (pdfKey changes)
  // 3. Mode change (previewMode changes)
  useEffect(() => {
    let cancelled = false;

    if (!snapshotData) {
      setProcessedFindings(null);
      setPdfProcessing(false);
      return;
    }

    // If switching to executive mode, no need to process findings
    if (previewMode === 'executive') {
      setProcessedFindings(null);
      setPdfProcessing(false);
      return;
    }

    const processAllFindings = async () => {
      // If findings array and config have not changed, reuse cached processed result instantly (0ms)!
      if (
        lastProcessedResultRef.current &&
        snapshotData.detailed_findings === lastDetailedFindingsRef.current &&
        snapshotData.finding_config === lastConfigRef.current
      ) {
        setProcessedFindings(lastProcessedResultRef.current);
        setPdfProcessing(false);
        return;
      }

      setPdfProcessing(true);
      const config = snapshotData.finding_config;
      const result: Record<string, any[]> = {};
      
      // Pre-calculate keys for all findings relative to their original position
      const allFindingsWithKeys = (snapshotData.detailed_findings || []).map((f, idx) => ({
        ...f,
        _stableKey: getFindingStableKey(f, idx)
      }));

      const severities = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'] as const;

      await Promise.all(severities.map(async (severity) => {
        const findings = allFindingsWithKeys
          .filter(f => {
            const normalized = (f.severity_category || 'Info').trim().toUpperCase();
            if (normalized === 'NONE' || normalized === '') return severity === 'INFO';
            return normalized === severity;
          })
          .filter((f) => {
            const idStr = (f as any)._stableKey;
            const indConfig = config?.individualFindings?.[idStr];
            
            if (indConfig) {
              return indConfig.included;
            }
            
            // Default: Only include OPEN findings if no individual override exists
            return isFindingActuallyOpen(snapshotData, f);
          });
          
        const processedFindingsList = await Promise.all(findings.map(async (finding) => {
          // Cache key based on stable key + finding content fields
          const cacheKey = `${(finding as any)._stableKey || ''}_${(finding as any).title || ''}_${(finding as any).description || ''}_${(finding as any).instructions || ''}_${(finding as any).impact || ''}_${(finding as any).fix || ''}_${JSON.stringify((finding as any).attachments || [])}`;
          if (parsedFindingCache.current.has(cacheKey)) {
            return parsedFindingCache.current.get(cacheKey);
          }

          const processed: Record<string, any> = {};
          const fields = ['description', 'instructions', 'impact', 'fix'] as const;
          
          await Promise.all(fields.map(async (field) => {
            if ((finding as any)[field]) {
              processed[field] = await parseHtmlToElements((finding as any)[field]);
            }
          }));
          
          // Process attachments (evidence)
          if ((finding as any).attachments) {
            processed.evidenceElements = processAttachmentsToElements((finding as any).attachments);
          }
          
          const processedFinding = { ...finding, ...processed };
          parsedFindingCache.current.set(cacheKey, processedFinding);
          return processedFinding;
        }));

        result[severity] = processedFindingsList;
      }));

      if (!cancelled) {
        lastDetailedFindingsRef.current = snapshotData.detailed_findings;
        lastConfigRef.current = config;
        lastProcessedResultRef.current = result;
        setProcessedFindings(result);
        setPdfProcessing(false);
      }
    };

    // Only process if initialized (allows initial render) OR if pdfKey > 0 (refresh)
    if (isInitialized || pdfKey > 0) {
      processAllFindings();
    }

    return () => { cancelled = true; };
  }, [pdfKey, previewMode, isInitialized, snapshotData]); // Trigger on refresh, mode change, or initial load

  // Handle download button click
  const handleDownload = () => {
    if (pdfProcessing) {
      setPendingDownload(true);
    } else {
      if (pdfDownloaderRef.current && pdfDownloaderRef.current.downloadPDF) {
        pdfDownloaderRef.current.downloadPDF();
      }
    }
  };

  // When processing finishes and download is pending, trigger download
  useEffect(() => {
    if (!pdfProcessing && pendingDownload) {
      setPendingDownload(false);
      if (pdfDownloaderRef.current && pdfDownloaderRef.current.downloadPDF) {
        pdfDownloaderRef.current.downloadPDF();
      }
    }
  }, [pdfProcessing, pendingDownload]);



  // Memoize chart data to prevent unnecessary re-renders
  // Only recalculate when snapshotData changes (manual refresh updates snapshot)
  const pieData = useMemo(() => ({
    labels: ['Critical', 'High', 'Medium', 'Low', 'Info'],
    datasets: [{
      data: [
        snapshotData?.open_close_counts_by_severity?.Critical?.Total || 0,
        snapshotData?.open_close_counts_by_severity?.High?.Total || 0,
        snapshotData?.open_close_counts_by_severity?.Medium?.Total || 0,
        snapshotData?.open_close_counts_by_severity?.Low?.Total || 0,
        snapshotData?.open_close_counts_by_severity?.Info?.Total || 0,
      ],
      backgroundColor: [
        '#dc2626', // Modern red for Critical
        '#ea580c', // Modern orange for High
        '#eab308', // Modern yellow for Medium
        '#22c55e', // Modern green for Low
        '#64748b', // Modern grey for Info
      ],
      borderWidth: 0,
      hoverOffset: 8,
      hoverBorderColor: '#ffffff',
      hoverBorderWidth: 3,
    }],
  }), [snapshotData]); // Only update when snapshot changes

  const barData = useMemo(() => ({
    labels: ['Critical', 'High', 'Medium', 'Low', 'Info'],
    datasets: [{
      label: 'Number of Findings',
      data: [
        snapshotData?.open_close_counts_by_severity?.Critical?.Total || 0,
        snapshotData?.open_close_counts_by_severity?.High?.Total || 0,
        snapshotData?.open_close_counts_by_severity?.Medium?.Total || 0,
        snapshotData?.open_close_counts_by_severity?.Low?.Total || 0,
        snapshotData?.open_close_counts_by_severity?.Info?.Total || 0,
      ],
      backgroundColor: [
        '#dc2626', // Modern red for Critical
        '#ea580c', // Modern orange for High
        '#eab308', // Modern yellow for Medium
        '#22c55e', // Modern green for Low
        '#64748b', // Modern grey for Info
      ],
      borderWidth: 0,
      borderRadius: 8,
      borderSkipped: false,
      hoverBackgroundColor: [
        '#b91c1c', // Darker red on hover
        '#c2410c', // Darker orange on hover
        '#ca8a04', // Darker yellow on hover
        '#16a34a', // Darker green on hover
        '#475569', // Darker grey on hover
      ],
    }],
  }), [snapshotData]); // Only update when snapshot changes

  return (
    <div className={`relative h-full flex flex-col ${hideHeader ? 'bg-transparent' : 'bg-gradient-to-br from-blue-50/80 to-blue-100/60'}`}>
      {/* Glassy Sticky Header */}
      {!hideHeader && (
        <div className="sticky top-0 z-20 w-full bg-blue-700/80 backdrop-blur-md shadow-lg px-6 py-2 flex items-center justify-between rounded-b-xl border-b border-blue-200/30">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-lg">
              <svg className="w-5 h-5 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <h2 className="text-base font-bold text-white tracking-tight drop-shadow">Preview</h2>

            {/* Report Type Switch */}
            <div className="flex items-center gap-2 ml-4">
              <div className="flex bg-white/20 rounded-lg p-1">
                {(() => {
                  const modes = getAvailablePreviewModes(snapshotData);
                  return (
                    <>
                      {modes.includes('executive') && (
                        <button
                          onClick={() => handlePreviewModeChange('executive')}
                          className={`px-3 py-1 rounded text-xs font-semibold transition-all duration-200 ${previewMode === 'executive'
                            ? 'bg-white text-blue-700 shadow-sm'
                            : 'text-white/80 hover:text-white hover:bg-white/10'
                            }`}
                        >
                          Executive Summary
                        </button>
                      )}
                      {modes.includes('technical') && (
                        <button
                          onClick={() => handlePreviewModeChange('technical')}
                          className={`px-3 py-1 rounded text-xs font-semibold transition-all duration-200 ${previewMode === 'technical'
                            ? 'bg-white text-blue-700 shadow-sm'
                            : 'text-white/80 hover:text-white hover:bg-white/10'
                            }`}
                        >
                          Technical Summary
                        </button>
                      )}
                      {modes.includes('full') && (
                        <button
                          onClick={() => handlePreviewModeChange('full')}
                          className={`px-3 py-1 rounded text-xs font-semibold transition-all duration-200 ${previewMode === 'full'
                            ? 'bg-white text-blue-700 shadow-sm'
                            : 'text-white/80 hover:text-white hover:bg-white/10'
                            }`}
                        >
                          Full Report
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>

            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Vanta Controls in Header (optional) */}
            {onUploadToVanta && role === UserRole.BUSINESS && vantaConnected && (
              <div className="ml-2">
                <button
                  onClick={onUploadToVanta}
                  title="Upload to Vanta"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-semibold shadow-sm hover:bg-purple-700 disabled:opacity-60"
                >
                  <img
                    src="/vanta-logo.jpg"
                    alt="Vanta"
                    className="w-3 h-3 rounded object-contain bg-white"
                  />
                  {vantaUploadLoading ? `Uploading... ${Math.max(0, Math.min(100, vantaUploadProgress || 0))}%` : 'Upload to Vanta'}
                </button>
              </div>
            )}
            {hasUnrefreshedChanges && (
              <div className="flex items-center gap-2 px-3 py-1 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
                <svg className="w-4 h-4 text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-xs text-yellow-100 font-medium">Changes not reflected</span>
              </div>
            )}
            <button
              onClick={handlePdfRefresh}
              title="Refresh PDF Preview"
              className={`flex items-center justify-center p-2 rounded-lg transition-colors shadow-sm ${hasUnrefreshedChanges
                ? 'bg-yellow-500/30 hover:bg-yellow-500/40 animate-pulse'
                : 'bg-white/20 hover:bg-white/30'
                }`}
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>


            {snapshotData && (
              <>
                <PDFDownloader
                  ref={pdfDownloaderRef}
                  reportData={snapshotData}
                  processedFindings={processedFindings}
                  fileName={`${snapshotData.company_name}-${previewMode === 'full' ? 'full' : previewMode === 'executive' ? 'executive-summary' : 'technical'}-security-report.pdf`}
                  compact={true}
                  onClick={handleDownload}
                  disabled={pdfProcessing && !pendingDownload}
                  previewMode={previewMode}
                  pieChartImage={pieChartImage}
                  barChartImage={barChartImage}
                />
                {pendingDownload && (
                  <span className="ml-2 text-xs text-blue-700 animate-pulse">Preparing PDF...</span>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {/* Preview Area */}
      <div className={`flex-1 overflow-auto ${hideHeader ? 'p-0' : 'p-4 md:p-8'} ${hideHeader ? 'bg-transparent' : 'bg-gradient-to-br from-blue-50/80 to-blue-100/60'}`}>
        <div className="flex flex-col items-center w-full min-h-[80vh]">
          {previewLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur rounded-xl z-10">
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600"></div>
            </div>
          ) : (
            <div className="w-full h-full">
              {snapshotData ? (
                <>

                  {!pieChartImage && (
                    <ChartImageGenerator
                      type="pie"
                      data={pieData}
                      onImageReady={setPieChartImage}
                    />
                  )}
                  {!barChartImage && (
                    <ChartImageGenerator
                      type="bar"
                      data={barData}
                      options={{
                        scales: { y: { beginAtZero: true, ticks: { precision: 0 } }, },
                        plugins: { legend: { display: false } },
                      }}
                      onImageReady={setBarChartImage}
                    />
                  )}
                  {(previewMode === 'executive' || (pieChartImage && barChartImage)) ? (
                    <div className="w-full h-full">
                      {/* Check if Buffer is available before rendering PDF */}
                      {typeof (window as any).Buffer !== 'undefined' ? (
                        <PDFBlobViewer
                          pdfKey={pdfKey}
                          previewMode={previewMode}
                          snapshotData={snapshotData}
                          processedFindings={processedFindings}
                          pdfProcessing={pdfProcessing}
                          pieChartImage={pieChartImage || ''}
                          barChartImage={barChartImage || ''}
                          template={snapshotData?.template}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <div className="text-red-500 mb-4">
                              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-700 mb-2">PDF Rendering Error</h3>
                            <p className="text-gray-500 mb-4">Buffer polyfill not available. Please refresh the page.</p>
                            <button
                              onClick={() => window.location.reload()}
                              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              Refresh Page
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full min-h-[400px]">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600 mx-auto mb-4" />
                        <p className="text-gray-600">Generating charts & preview...</p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="text-gray-500 mb-4">
                      <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No Report Data</h3>
                    <p className="text-gray-500">Please ensure report data is loaded to view PDF preview.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      {snapshotData && hideHeader && (
        <PDFDownloader
          ref={pdfDownloaderRef}
          reportData={snapshotData}
          processedFindings={processedFindings}
          fileName={`${snapshotData.company_name}-${previewMode === 'full' ? 'full' : previewMode === 'executive' ? 'executive-summary' : 'technical'}-security-report.pdf`}
          compact={false}
          onClick={handleDownload}
          disabled={pdfProcessing && !pendingDownload}
          previewMode={previewMode}
          pieChartImage={pieChartImage}
          barChartImage={barChartImage}
        />
      )}
      </div>
    </div>
  );
}));

export default PreviewSection;